import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNotification } from '../common/NotificationContainer';
import Button from '../common/Button'; // Assuming Button component is used
import ConfirmationModal from '../common/ConfirmationModal';

const BulkActionModal = ({
  isOpen,
  onClose,
  selectedLeads,
  users,
  manualSegments = [], // Add manualSegments prop
  bulkAction, // Add bulkAction prop
  onExecuteBulkAction
}) => {
  const [bulkActionData, setBulkActionData] = useState({
    status: '',
    assigned_to: '',
    country: '',
    call_attempts: '',
    subscribed: '',
    segmentId: '' // Add state for selected segment ID
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { showNotification } = useNotification();
  const modalRef = useRef(null);

  // Ref to hold the latest bulkActionData to avoid stale closures in listener
  const latestBulkActionData = useRef(bulkActionData);

  // Effect to keep the ref updated whenever bulkActionData changes
  useEffect(() => {
    latestBulkActionData.current = bulkActionData;
  }, [bulkActionData]);

  // Show confirmation dialog first
  const handleShowConfirmation = () => {
    setShowConfirmation(true);
  };

  // Execute the actual action after confirmation
  const handleExecuteAction = () => {
    if (bulkAction === 'update') {
        onExecuteBulkAction('update', bulkActionData);
        showNotification(`Updated ${selectedLeads.length} lead${selectedLeads.length !== 1 ? 's' : ''} successfully`, 'success');
    } else if (bulkAction === 'addToSegment') {
        if (bulkActionData.segmentId) {
            onExecuteBulkAction('addToSegment', { segmentId: bulkActionData.segmentId });
            // Find segment name for notification
            const segment = manualSegments.find(s => s.id.toString() === bulkActionData.segmentId);
            const segmentName = segment ? segment.name : 'the selected segment';
            showNotification(`Added ${selectedLeads.length} lead${selectedLeads.length !== 1 ? 's' : ''} to ${segmentName}`, 'success');
        } else {
             showNotification('Please select a segment.', 'error');
             setShowConfirmation(false);
             return; // Don't close modal if no segment selected
        }
    }
    setShowConfirmation(false);
    resetAndClose();
  };

  const resetAndClose = () => {
    setBulkActionData({
      status: '',
      assigned_to: '',
      country: '',
      call_attempts: '',
      subscribed: '',
      segmentId: ''
    });
    onClose();
  };

  // Determine if submit should be disabled based on action
  const isSubmitDisabled = bulkAction === 'update'
    ? (!bulkActionData.status &&
       !bulkActionData.assigned_to &&
       !bulkActionData.country &&
       !bulkActionData.call_attempts &&
       !bulkActionData.subscribed)
    : bulkAction === 'addToSegment'
    ? !bulkActionData.segmentId // Disable if no segment is selected
    : true; // Disable for unknown actions
    
  const modalTitle = bulkAction === 'addToSegment'
    ? `Add ${selectedLeads.length} Lead${selectedLeads.length !== 1 ? 's' : ''} to Segment`
    : `Update ${selectedLeads.length} Lead${selectedLeads.length !== 1 ? 's' : ''}`;

  const submitButtonText = bulkAction === 'addToSegment' ? 'Add to Segment' : 'Update Leads';

  // Reset state when modal opens or action changes
  useEffect(() => {
    if (isOpen) {
      setBulkActionData({
        status: '',
        assigned_to: '',
        country: '',
        call_attempts: '',
        subscribed: '',
        segmentId: '' // Reset segmentId as well
      });
      // Focus the modal container when it opens to help capture key events
      // Moved from the keydown effect to run only once on open
      modalRef.current?.focus();
    }
  }, [isOpen, bulkAction]); // Removed modalRef from here if it was added

  // Setup keyboard handlers
  useEffect(() => {
    if (!isOpen) return;

    // Setup GLOBAL keyboard handler
    const handleGlobalKeyDown = (e) => { // Renamed back for clarity
      // Only process Enter key
      if (e.key === 'Enter') {
        const targetElement = e.target;
        // Allow Enter from the 'bulkCountry' input, but ignore from others like SELECT/TEXTAREA
        if (
            (targetElement.tagName === 'SELECT') ||
            (targetElement.tagName === 'TEXTAREA') ||
            // Ignore INPUT elements unless they are 'bulkCountry' or 'bulkCallAttempts'
            (targetElement.tagName === 'INPUT' && targetElement.id !== 'bulkCountry' && targetElement.id !== 'bulkCallAttempts') ||
            targetElement.isContentEditable
           ) {
          return; // Ignore Enter press in these specific elements
        }

        // Calculate if button should be enabled using the LATEST data from the ref
        const currentData = latestBulkActionData.current;
        const updateDisabled = bulkAction === 'update'
          ? (!currentData.status &&
             !currentData.assigned_to &&
             !currentData.country &&
             !currentData.call_attempts &&
             !currentData.subscribed)
          : bulkAction === 'addToSegment'
          ? !currentData.segmentId
          : true;

        // If not disabled, proceed with confirmation
        if (!updateDisabled) {
          e.preventDefault(); // Prevent default Enter behavior (like form submission)
          e.stopPropagation(); // Stop the event from bubbling further
          setShowConfirmation(true);
        }
      }
    };

    // Add listener to the window
    window.addEventListener('keydown', handleGlobalKeyDown);

    // DO NOT Focus the modal container here

    return () => {
      // Remove listener from the window
      window.removeEventListener('keydown', handleGlobalKeyDown);
    };
    // Only dependent on modal visibility and action type
  }, [isOpen, bulkAction]); // bulkActionData is NOT needed here

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center"
        // onClick={resetAndClose} // Optional: Close on backdrop click
      >
        <div
          ref={modalRef}
          // Add tabIndex to make the div focusable
          tabIndex="-1"
          // Prevent backdrop click from closing if clicking inside modal
          onClick={(e) => e.stopPropagation()}
          className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 md:mx-0 p-6 outline-none" // Added outline-none
        >
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">{modalTitle}</h3>
            {bulkAction === 'update' && (
              <p className="text-sm text-gray-600">
                Update properties for {selectedLeads.length} selected lead{selectedLeads.length !== 1 ? 's' : ''}:
              </p>
            )}
             {bulkAction === 'addToSegment' && (
              <p className="text-sm text-gray-600">
                Choose a manual segment to add the selected leads to.
              </p>
            )}
          </div>

          <div className="space-y-4 mb-6">
            {/* Conditional Rendering based on bulkAction */}
            {bulkAction === 'update' && (
              <div className="space-y-4">
                {/* Status */}
                <div>
                  <label htmlFor="bulkStatus" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    id="bulkStatus"
                    value={bulkActionData.status}
                    onChange={(e) => setBulkActionData({...bulkActionData, status: e.target.value})}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">No change</option>
                    <option value="new">New</option>
                    <option value="connected">Connected</option>
                    <option value="HOT">HOT</option>
                    <option value="unreachable">Unreachable</option>
                    <option value="disqualified">Disqualified</option>
                    <option value="converted">Converted</option>
                  </select>
                </div>
                {/* Assign To */}
                <div>
                  <label htmlFor="bulkAssignedTo" className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                  <select
                    id="bulkAssignedTo"
                    value={bulkActionData.assigned_to}
                    onChange={(e) => setBulkActionData({...bulkActionData, assigned_to: e.target.value})}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">No change</option>
                    <option value="null">Unassign</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>{user.name}</option>
                    ))}
                  </select>
                </div>
                {/* Country */}
                <div>
                  <label htmlFor="bulkCountry" className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <input
                    type="text"
                    id="bulkCountry"
                    value={bulkActionData.country}
                    onChange={(e) => setBulkActionData({...bulkActionData, country: e.target.value})}
                    placeholder="e.g. Norway, Sweden, United States, Germany"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                {/* Call Attempts */}
                <div>
                  <label htmlFor="bulkCallAttempts" className="block text-sm font-medium text-gray-700 mb-1">Call Attempts</label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      id="bulkCallAttempts"
                      min="0"
                      max="10"
                      value={bulkActionData.call_attempts}
                      onChange={(e) => setBulkActionData({...bulkActionData, call_attempts: e.target.value})}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                    <span className="ml-2 text-xs text-gray-500">0-10</span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Sets the exact value for all selected leads. Leave empty for no change.
                  </p>
                </div>
                {/* Subscribed */}
                <div>
                  <label htmlFor="bulkSubscribed" className="block text-sm font-medium text-gray-700 mb-1">Email Subscription</label>
                  <select
                    id="bulkSubscribed"
                    value={bulkActionData.subscribed}
                    onChange={(e) => setBulkActionData({...bulkActionData, subscribed: e.target.value})}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">No change</option>
                    <option value="true">Subscribed</option>
                    <option value="false">Unsubscribed</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Controls whether leads can receive marketing emails and notifications.
                  </p>
                </div>
              </div>
            )}

            {bulkAction === 'addToSegment' && (
              <div>
                <label htmlFor="bulkSegment" className="block text-sm font-medium text-gray-700 mb-1">Select Manual Segment</label>
                <select
                  id="bulkSegment"
                  value={bulkActionData.segmentId}
                  onChange={(e) => setBulkActionData({...bulkActionData, segmentId: e.target.value})}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required // Make selection required for this action
                >
                  <option value="">-- Choose a segment --</option>
                  {manualSegments.length > 0 ? (
                    manualSegments.map(segment => (
                      <option key={segment.id} value={segment.id}>
                        {segment.name} ({segment.is_public ? 'Public' : 'Private'})
                      </option>
                    ))
                  ) : (
                    <option disabled>No manual segments found</option>
                  )}
                </select>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button" // Ensure cancel button doesn't submit form
              onClick={resetAndClose}
              className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
            >
              Cancel
            </button>

            <button
              type="button" // Use type="button" to prevent default form submission
              onClick={handleShowConfirmation}
              disabled={isSubmitDisabled}
              className={`px-4 py-2 text-white rounded-md shadow-sm focus:outline-none ${
                isSubmitDisabled
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {submitButtonText}
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleExecuteAction}
        title={bulkAction === 'addToSegment' 
          ? `Confirm Adding ${selectedLeads.length} Lead${selectedLeads.length !== 1 ? 's' : ''} to Segment` 
          : `Confirm Updating ${selectedLeads.length} Lead${selectedLeads.length !== 1 ? 's' : ''}`}
        message={`Are you sure you want to ${bulkAction === 'addToSegment' ? 'add' : 'update'} ${selectedLeads.length} lead${selectedLeads.length !== 1 ? 's' : ''}? This action can't be automatically undone.`}
        confirmButtonText={bulkAction === 'addToSegment' ? 'Add to Segment' : 'Update Leads'}
        confirmButtonClass="bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
      />
    </>
  );
};

BulkActionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedLeads: PropTypes.array.isRequired,
  users: PropTypes.array.isRequired,
  manualSegments: PropTypes.array, // Add prop type for manual segments
  bulkAction: PropTypes.string.isRequired, // Action type is required
  onExecuteBulkAction: PropTypes.func.isRequired
};

export default BulkActionModal;
